import React, { useState, useEffect, useCallback } from 'react';

import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Link from '@mui/material/Link';

import { smGet, smPost, smPut, smDelete, errorText, SIZE_RANGES, sizeLabel, personName } from './api.js';

const COUNTRIES = ['United States', 'United Kingdom', 'Germany', 'France', 'Netherlands', 'Belgium', 'Italy', 'Spain', 'Switzerland', 'Austria',
    'Denmark', 'Sweden', 'Norway', 'Finland', 'Ireland', 'Poland', 'Portugal', 'Czech Republic', 'Israel', 'Canada', 'Mexico', 'Brazil',
    'Australia', 'New Zealand', 'India', 'Singapore', 'Japan', 'South Korea', 'China', 'United Arab Emirates', 'Turkey', 'South Africa',
    'Europe', 'North America', 'Asia', 'Middle East'];

function Chips({ label, value, onChange, options = [], placeholder }) {
    return (
        <Autocomplete
            multiple freeSolo size="small" options={options} value={value || []}
            onChange={(e, v) => onChange(v.map(s => String(s).trim()).filter(Boolean))}
            renderTags={(v, getTagProps) => v.map((o, i) => <Chip size="small" label={o} {...getTagProps({ index: i })} key={o} />)}
            renderInput={p => <TextField {...p} label={label} placeholder={placeholder} helperText="Type and press Enter to add" />}
        />
    );
}

function AudienceTab({ settings, onCampaignCreated }) {
    const [list, setList] = useState([]);
    const [audience, setAudience] = useState(null);
    const [draft, setDraft] = useState(null); // name, prompt, filters being edited
    const [busy, setBusy] = useState(null);
    const [error, setError] = useState(null);
    const [info, setInfo] = useState(null);
    const [confirmReveal, setConfirmReveal] = useState(false);
    const [domainsText, setDomainsText] = useState(null); // the websites box while it's being typed in

    const loadList = useCallback(async () => {
        try { setList(await smGet('/audiences')); } catch (e) { setError(errorText(e)); }
    }, []);
    useEffect(() => { loadList(); }, [loadList]);

    const open = async id => {
        setError(null); setInfo(null);
        try {
            const a = await smGet(`/audiences/${id}`);
            setAudience(a);
            setDraft({ name: a.name, prompt: a.prompt || '', filters: a.filters || {} });
        } catch (e) { setError(errorText(e)); }
    };

    const run = async (label, fn) => {
        setBusy(label); setError(null); setInfo(null);
        try { await fn(); } catch (e) { setError(errorText(e)); }
        setBusy(null);
    };

    const create = () => run('create', async () => {
        const a = await smPost('/audiences', { name: 'New audience' });
        await loadList();
        await open(a.id);
    });

    const fromPrompt = () => run('prompt', async () => {
        const f = await smPost('/filters-from-prompt', { prompt: draft.prompt });
        setDraft(d => ({ ...d, name: d.name === 'New audience' && f.name ? f.name : d.name, filters: { titles: f.titles, personLocations: f.personLocations, organizationLocations: f.organizationLocations || [], sizes: f.sizes, keywords: f.keywords, domains: d.filters?.domains || [] } }));
        setInfo('Filters filled in from your description. Check them, then save and find people.');
    });

    const dirty = audience && draft && JSON.stringify({ name: audience.name, prompt: audience.prompt || '', filters: audience.filters || {} }) !== JSON.stringify(draft);
    const save = () => run('save', async () => {
        const filtersChanged = JSON.stringify(audience.filters || {}) !== JSON.stringify(draft.filters);
        const a = await smPut(`/audiences/${audience.id}`, { name: draft.name, prompt: draft.prompt, ...(filtersChanged ? { filters: draft.filters } : {}) });
        setAudience(a);
        setDraft({ name: a.name, prompt: a.prompt || '', filters: a.filters || {} });
        loadList();
    });

    const search = () => run('search', async () => {
        if (dirty) await save();
        const r = await smPost(`/audiences/${audience.id}/search`, { perPage: 50 });
        setAudience(r.audience);
        setInfo(r.added ? `Found ${r.added} more people${r.total != null ? ` (${r.total.toLocaleString('en')} match in Apollo)` : ''}. No credits used.` : 'No more new people for these filters.');
        loadList();
    });

    const contacts = audience?.contacts || [];
    const chosenToReveal = contacts.filter(c => c.selected && !c.revealed);
    const ready = contacts.filter(c => c.selected !== false && c.revealed && c.emailStatus === 'verified');

    const select = (ids, selected) => run('select', async () => {
        setAudience(await smPost(`/audiences/${audience.id}/select`, { ids, selected }));
    });
    const reveal = () => run('reveal', async () => {
        setConfirmReveal(false);
        const r = await smPost(`/audiences/${audience.id}/reveal`, { ids: chosenToReveal.map(c => c.id) });
        setAudience(r.audience);
        setInfo(`Revealed ${r.revealed} emails, ${r.verified} verified. ${r.credits} Apollo credits used.`);
        loadList();
    });
    const removeUnchosen = () => run('remove', async () => {
        setAudience(await smPost(`/audiences/${audience.id}/remove`, { ids: contacts.filter(c => !c.selected && !c.revealed).map(c => c.id) }));
        loadList();
    });
    const remove = () => run('delete', async () => {
        await smDelete(`/audiences/${audience.id}`);
        setAudience(null); setDraft(null);
        loadList();
    });
    const createCampaign = () => run('campaign', async () => {
        const c = await smPost('/campaigns', { audienceId: audience.id, name: audience.name });
        onCampaignCreated(c.id);
    });

    const allChecked = contacts.length > 0 && contacts.every(c => c.selected);
    const f = draft?.filters || {};
    const setF = (k, v) => setDraft(d => ({ ...d, filters: { ...d.filters, [k]: v } }));

    const summary = {
        found: contacts.length,
        chosen: contacts.filter(c => c.selected).length,
        verified: contacts.filter(c => c.emailStatus === 'verified').length,
    };

    return (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <Paper variant="outlined" sx={{ width: 260, flexShrink: 0 }}>
                <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle2">Audiences</Typography>
                    <Button size="small" variant="contained" onClick={create} disabled={!!busy}>New</Button>
                </Box>
                <List dense disablePadding>
                    {list.map(a => (
                        <ListItemButton key={a.id} selected={audience?.id === a.id} onClick={() => open(a.id)}>
                            <ListItemText primary={a.name} secondary={`${a.people} found · ${a.verified} verified emails`} />
                        </ListItemButton>
                    ))}
                    {!list.length && <Typography variant="body2" color="text.secondary" sx={{ p: 1.5 }}>No audiences yet.</Typography>}
                </List>
            </Paper>

            <Box sx={{ flex: 1, minWidth: 0 }}>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {info && <Alert severity="success" sx={{ mb: 2 }}>{info}</Alert>}
                {settings && !settings.apollo && <Alert severity="warning" sx={{ mb: 2 }}>APOLLO_API_KEY isn't set on this server, so searching Apollo won't work here.</Alert>}
                {!audience && <Typography color="text.secondary">Choose an audience on the left, or create a new one.</Typography>}
                {audience && draft && (
                    <>
                        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                            <TextField label="Audience name" size="small" fullWidth value={draft.name} onChange={e => setDraft(d => ({ ...d, name: e.target.value }))} sx={{ mb: 2 }} />
                            <TextField
                                label="Describe the audience" multiline minRows={2} fullWidth value={draft.prompt}
                                onChange={e => setDraft(d => ({ ...d, prompt: e.target.value }))}
                                placeholder="e.g. Heads of logistics and supply chain at food, beverage and packaging manufacturers in Germany and the Netherlands, 200 to 5,000 employees"
                            />
                            <Box sx={{ mt: 1, mb: 2 }}>
                                <Button size="small" onClick={fromPrompt} disabled={!draft.prompt.trim() || !!busy}>
                                    {busy === 'prompt' ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}Fill in the filters from the description
                                </Button>
                            </Box>
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                                <Chips label="Job titles" value={f.titles} onChange={v => setF('titles', v)} placeholder="Head of Logistics" />
                                <Chips label="Industry keywords" value={f.keywords} onChange={v => setF('keywords', v)} placeholder="food, packaging" />
                                <Chips label="People in (countries or regions)" value={f.personLocations} onChange={v => setF('personLocations', v)} options={COUNTRIES} />
                                <Chips label="Company headquarters in (optional)" value={f.organizationLocations} onChange={v => setF('organizationLocations', v)} options={COUNTRIES} />
                            </Box>
                            <TextField
                                label={`Only at these companies (optional)${(f.domains || []).length ? `: ${f.domains.length}` : ''}`}
                                multiline minRows={2} maxRows={8} fullWidth sx={{ mt: 2 }}
                                value={domainsText ?? (f.domains || []).join('\n')}
                                onChange={e => setDomainsText(e.target.value)}
                                onBlur={() => { if (domainsText !== null) { setF('domains', domainsText.split(/[\s,]+/).map(s => s.trim()).filter(Boolean)); setDomainsText(null); } }}
                                placeholder={'acme.com\nexample.co.uk'}
                                helperText="Company websites, one per line. The search then finds people only at these companies, for example a partner list taken from a website."
                            />
                            <Typography variant="body2" sx={{ mt: 2, mb: 0.5 }}>Company size (employees)</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                                {SIZE_RANGES.map(r => (
                                    <FormControlLabel key={r} label={sizeLabel(r)} control={<Checkbox size="small" checked={(f.sizes || []).includes(r)} onChange={e => setF('sizes', e.target.checked ? [...(f.sizes || []), r] : (f.sizes || []).filter(x => x !== r))} />} />
                                ))}
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                                <Button variant="outlined" onClick={save} disabled={!dirty || !!busy}>Save</Button>
                                <Button variant="contained" onClick={search} disabled={!!busy}>
                                    {busy === 'search' ? <CircularProgress size={16} sx={{ mr: 1, color: 'inherit' }} /> : null}
                                    {contacts.length ? 'Find more people' : 'Find people'}
                                </Button>
                                <Box sx={{ flex: 1 }} />
                                <Button color="error" onClick={remove} disabled={!!busy}>Delete audience</Button>
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                Finding people uses no Apollo credits. Revealing an email uses one credit per person. Changing the filters starts the search again from the first page.
                            </Typography>
                        </Paper>

                        {contacts.length > 0 && (
                            <Paper variant="outlined">
                                <Box sx={{ p: 1.5, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                    <Typography variant="subtitle2" sx={{ mr: 1 }}>
                                        {summary.found} found{audience.total != null ? ` of ${audience.total.toLocaleString('en')}` : ''} · {summary.chosen} chosen · {summary.verified} verified emails
                                        {audience.creditsUsed ? ` · ${audience.creditsUsed} credits used` : ''}
                                    </Typography>
                                    <Button size="small" onClick={() => select(contacts.map(c => c.id), true)} disabled={!!busy || allChecked}>Select all</Button>
                                    <Button size="small" onClick={() => select(contacts.map(c => c.id), false)} disabled={!!busy || !contacts.some(c => c.selected)}>Deselect all</Button>
                                    <Box sx={{ flex: 1 }} />
                                    <Button size="small" onClick={removeUnchosen} disabled={!!busy || !contacts.some(c => !c.selected && !c.revealed)}>Remove the people not chosen</Button>
                                    <Button size="small" variant="outlined" onClick={() => setConfirmReveal(true)} disabled={!!busy || !chosenToReveal.length}>
                                        Reveal emails ({chosenToReveal.length})
                                    </Button>
                                    <Button size="small" variant="contained" onClick={createCampaign} disabled={!!busy || !ready.length}>
                                        Create a campaign ({ready.length} people)
                                    </Button>
                                </Box>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell padding="checkbox"><Checkbox size="small" checked={allChecked} indeterminate={!allChecked && contacts.some(c => c.selected)} onChange={e => select(contacts.map(c => c.id), e.target.checked)} /></TableCell>
                                            <TableCell>Person</TableCell>
                                            <TableCell>Country</TableCell>
                                            <TableCell>Role</TableCell>
                                            <TableCell>Email</TableCell>
                                            <TableCell>LinkedIn</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {contacts.map(c => (
                                            <TableRow key={c.id} hover>
                                                <TableCell padding="checkbox"><Checkbox size="small" checked={!!c.selected} onChange={e => select([c.id], e.target.checked)} /></TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{personName(c)}</Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {[c.title, c.company].filter(Boolean).join(' · ')}
                                                        {c.employees ? ` · ${c.employees.toLocaleString('en')} people` : ''}
                                                    </Typography>
                                                </TableCell>
                                                {/* The country from the revealed profile; before that, the location the search was run for. */}
                                                <TableCell>{c.country || c.location || ''}</TableCell>
                                                <TableCell>{c.role || ''}</TableCell>
                                                <TableCell>
                                                    {c.revealed
                                                        ? (c.email ? <>{c.email} <Chip size="small" label={c.emailStatus || 'unknown'} color={c.emailStatus === 'verified' ? 'success' : 'default'} variant="outlined" /></> : <Typography variant="caption" color="text.secondary">No email found</Typography>)
                                                        : <Typography variant="caption" color="text.secondary">{c.hasEmail ? 'Not revealed yet' : 'Apollo has no email'}</Typography>}
                                                </TableCell>
                                                <TableCell>{c.linkedin ? <Link href={c.linkedin} target="_blank" rel="noreferrer">Profile</Link> : ''}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Paper>
                        )}
                    </>
                )}
            </Box>

            <Dialog open={confirmReveal} onClose={() => setConfirmReveal(false)}>
                <DialogTitle>Reveal {chosenToReveal.length} emails?</DialogTitle>
                <DialogContent>
                    <Typography>This uses {chosenToReveal.length} Apollo credits, one per person. Only work emails are asked for, never personal emails or phone numbers. Only verified emails go into a campaign.</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmReveal(false)}>Cancel</Button>
                    <Button variant="contained" onClick={reveal}>Reveal and use {chosenToReveal.length} credits</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default AudienceTab;
